import React, { useState, useEffect, useMemo, useCallback } from 'react';
import BreederRequestModal from './BreederRequestModal';
import { useParticipants } from '../hooks/useParticipants';
import { sendHandlerRequest } from '../api/actions';
import { calculateHandlerOpenSlots, calculateAvailableSlotsForBreeder, isHandlerAtCapacity } from '../utils/handlerCapacity';
import { SaveParticipant, getHandlerRequestsByBreederId, cancelHandlerRequest } from '../api/actions';
import { getLoggedInParticipant } from '../api/participantAuth';

export default function BreederTab({ 
  participant, 
  onMessage, 
  onParticipantUpdate, 
  isEditing, 
  onEditingChange, 
  saving, 
  setSaving 
}) {
  // Local state
  const [breederData, setBreederData] = useState({
    totalBirds: '',
    handlerRequests: []
  });
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedHandler, setSelectedHandler] = useState(null);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestsLoaded, setRequestsLoaded] = useState(false);
  const [originalBreederData, setOriginalBreederData] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isTotalBirdsSaved, setIsTotalBirdsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showAdminRotationModal, setShowAdminRotationModal] = useState(false);
  const [adminHandler, setAdminHandler] = useState(null);
  const [showBirdModal, setShowBirdModal] = useState(false);
  const [selectedRequestForBirds, setSelectedRequestForBirds] = useState(null);
  
  // Get all participants for handlers
  const { participants: allParticipants } = useParticipants();

  // Initialize breeder data
  useEffect(() => {
    if (participant) {
      const initData = {
        totalBirds: participant.breederInfo?.totalBirdsForRace || participant.totalBirds || '',
        handlerRequests: participant.handlerRequests || []
      };
      setBreederData(initData);
      if (!originalBreederData) {
        setOriginalBreederData(initData);
      }
      
      // Mark as saved if participant already has total birds
      if (participant.breederInfo?.totalBirdsForRace || participant.totalBirds) {
        setIsTotalBirdsSaved(true);
      }
      
      // Reset unsaved changes when loading fresh data
      setHasUnsavedChanges(false);
    }
  }, [participant]);

  // Helper functions
  const hasRole = (roleToCheck) => {
    if (Array.isArray(participant?.role)) {
      return participant.role.some(r => r.toLowerCase() === roleToCheck.toLowerCase());
    }
    return participant?.role?.toLowerCase() === roleToCheck.toLowerCase();
  };

  // Memoize expensive calculations that depend on breeder data
  const getRequiredAdminRotationBirds = useMemo(() => {
    const totalBirds = parseInt(breederData.totalBirds) || 0;
    if (totalBirds >= 30) return 3;
    if (totalBirds >= 12) return 2;
    if (totalBirds >= 5) return 1;
    return 0;
  }, [breederData.totalBirds]);

  const getAvailableForHandlerRequests = useMemo(() => {
    const totalBirds = parseInt(breederData.totalBirds) || 0;
    return totalBirds - getRequiredAdminRotationBirds;
  }, [breederData.totalBirds, getRequiredAdminRotationBirds]);

  // Memoize handler-specific bird calculations
  const getApprovedBirdsForHandler = useCallback((handlerId) => {
    return breederData.handlerRequests
      .filter(r => r.handlerId === handlerId && r.status === 'approved')
      .reduce((total, r) => total + (r.numberOfBirds || r.birdsRequested || 0), 0);
  }, [breederData.handlerRequests]);

  const getPendingBirdsForHandler = useCallback((handlerId) => {
    return breederData.handlerRequests
      .filter(r => r.handlerId === handlerId && r.status === 'pending')
      .reduce((total, r) => total + (r.numberOfBirds || r.birdsRequested || 0), 0);
  }, [breederData.handlerRequests]);

  const getApprovedAndPendingBirdsForHandler = useCallback((handlerId) => {
    return getApprovedBirdsForHandler(handlerId) + getPendingBirdsForHandler(handlerId);
  }, [getApprovedBirdsForHandler, getPendingBirdsForHandler]);

  const getRemainingBirdSlotsForHandler = useCallback((handlerId) => {
    return 5 - getApprovedAndPendingBirdsForHandler(handlerId);
  }, [getApprovedAndPendingBirdsForHandler]);

  // Memoize total bird calculations
  const getTotalApprovedBirds = useMemo(() => {
    return breederData.handlerRequests
      .filter(r => r.status === 'approved')
      .reduce((total, r) => total + (r.numberOfBirds || r.birdsRequested || 0), 0);
  }, [breederData.handlerRequests]);

  const getTotalPendingBirds = useMemo(() => {
    return breederData.handlerRequests
      .filter(r => r.status === 'pending')
      .reduce((total, r) => total + (r.numberOfBirds || r.birdsRequested || 0), 0);
  }, [breederData.handlerRequests]);

  const getTotalRequestedRotationBirds = useMemo(() => {
    const rotationRequests = breederData.handlerRequests
      .filter(r => (r.handlerId === 'admin-rotation' || r.handlerId === 'admin-rotation-mandatory' || r.isAdminRotation === true) && (r.status === 'pending' || r.status === 'approved'));
    
    return rotationRequests.reduce((total, r) => total + (r.birdsRequested || r.numberOfBirds || 0), 0);
  }, [breederData.handlerRequests]);

  // Memoize mandatory rotation status
  const getMandatoryRotationStatus = useMemo(() => {
    const requiredBirds = getRequiredAdminRotationBirds;
    if (requiredBirds === 0) return { status: 'none', birds: 0 };

    const mandatoryRequest = breederData.handlerRequests
      .find(r => r.handlerId === 'admin-rotation-mandatory');

    if (!mandatoryRequest) {
      return { status: 'needed', birds: requiredBirds };
    }

    return {
      status: mandatoryRequest.status, // 'pending' or 'approved'
      birds: mandatoryRequest.numberOfBirds || mandatoryRequest.birdsRequested || 0
    };
  }, [getRequiredAdminRotationBirds, breederData.handlerRequests]);

  const getBirdsInfo = useCallback((request) => {
    // For breeder view, we would get bird info from their own data
    // This would be connected to their bird management system
    return request.birds || [];
  }, []);

  const handleViewBirdInfo = useCallback((request) => {
    setSelectedRequestForBirds(request);
    setShowBirdModal(true);
  }, []);

  // Memoize remaining birds calculation
  const getRemainingBirdsForHandlers = useMemo(() => {
    const availableForHandlers = getAvailableForHandlerRequests;
    const alreadyAssignedOrPending = getTotalApprovedBirds + getTotalPendingBirds;
    const rotationRequests = getTotalRequestedRotationBirds;
    return Math.max(0, availableForHandlers - alreadyAssignedOrPending - rotationRequests);
  }, [getAvailableForHandlerRequests, getTotalApprovedBirds, getTotalPendingBirds, getTotalRequestedRotationBirds]);

  const calculateBirds = useMemo(() => {
    const total = parseInt(breederData.totalBirds) || 0;
    const requiredRotation = getRequiredAdminRotationBirds;
    const toRotation = requiredRotation; // Automatically use required rotation
    const availableForHandlers = getAvailableForHandlerRequests;
    const approvedBirds = getTotalApprovedBirds;
    const rotationRequests = getTotalRequestedRotationBirds;
    // Calculation: Total - Rotation - Approved = Un-assigned
    const remainingForHandlers = Math.max(0, total - toRotation - approvedBirds);
    
    return {
      total,
      toRotation,
      requiredRotation,
      availableForHandlers,
      approvedBirds,
      remainingForHandlers,
      isValid: total >= 5
    };
  }, [breederData.totalBirds, getRequiredAdminRotationBirds, getAvailableForHandlerRequests, getTotalApprovedBirds, getTotalRequestedRotationBirds]);

  const getAvailableHandlers = useMemo(() => {
    if (!allParticipants || !participant) return [];
    
    return allParticipants.filter(p => {
      const hasHandlerRole = Array.isArray(p.role) 
        ? p.role.some(r => r.toLowerCase() === 'handler')
        : p.role?.toLowerCase() === 'handler';
      
      const isInSameSection = p.section === participant.section;
      const isNotSelf = p.id !== participant.id;
      
      return hasHandlerRole && isInSameSection && isNotSelf;
    }).sort((a, b) => {
      const nameA = (a.fullName || '').toLowerCase();
      const nameB = (b.fullName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [allParticipants, participant]);

  const getRequestStatus = useCallback((handlerId) => {
    const request = breederData.handlerRequests.find(r => r.handlerId === handlerId);
    // Treat cancelled requests as if no request exists (allow new requests)
    if (!request || request.status === 'cancelled') {
      return 'none';
    }
    return request.status;
  }, [breederData.handlerRequests]);

  // Event handlers
  const handleInputChange = (field, value) => {
    if (!isEditing) return;
    
    setBreederData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
  };

  const handleSendRequest = async (requestData) => {
    try {
      setLoadingRequests(true);
      
      // Check if total birds are saved - be more flexible here
      const totalBirds = participant.totalBirds || participant.breederInfo?.totalBirdsForRace || breederData.totalBirds;
      if (!totalBirds || totalBirds < 5) {
        onMessage('Please save your total birds count (minimum 5) before sending requests.', 'error');
        return;
      }
      
      // Validate max 5 birds per handler (pending + approved)
      const currentTotal = getApprovedAndPendingBirdsForHandler(requestData.handlerId);
      const requested = Number(requestData.numberOfBirds) || 0;
      
      if (currentTotal + requested > 5) {
        onMessage('You cannot send more than 5 birds to the same handler (including pending and approved requests).', 'error');
        return;
      }
      
      // Validate total birds don't exceed available slots
      const remainingSlots = getRemainingBirdsForHandlers;
      
      if (requested > remainingSlots) {
        onMessage(`You can only request ${remainingSlots} more birds for handlers. ${getRequiredAdminRotationBirds()} birds must go to admin rotation.`, 'error');
        return;
      }
      
      // Validate request data before sending
      if (!requestData.handlerId) {
        onMessage('Handler ID is required', 'error');
        return;
      }
      if (!requestData.numberOfBirds || requestData.numberOfBirds <= 0) {
        onMessage('Number of birds must be greater than 0', 'error');
        return;
      }
      
      const result = await sendHandlerRequest(participant.id, requestData.handlerId, requestData);
      
      if (result.success) {
        onMessage('Request sent successfully! The handler will review your request.', 'success');
        
        // Reload breeder requests from server to get the actual data
        await loadBreederRequests(true); // Force reload
        
        // Reload participant data to update slot counts like original dashboard
        if (typeof onParticipantUpdate === 'function') {
          await onParticipantUpdate();
        }
        
        setShowRequestModal(false);
        setSelectedHandler(null);
      } else {
        onMessage(`Failed to send request: ${result.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Send request error:', error);
      onMessage('Failed to send handler request. Please try again.', 'error');
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this handler request?')) {
      return;
    }

    try {
      setLoadingRequests(true);
      
      // Call API to cancel the request (calls UpdateHandlerRequestStatus with status='cancelled')
      const result = await cancelHandlerRequest(participant.id, requestId);
      
      if (result.success) {
        onMessage('Request cancelled successfully!', 'success');
        
        // Reload requests from server to get updated data
        await loadBreederRequests(true);
        
        // Reload participant data to update available birds count
        if (typeof onParticipantUpdate === 'function') {
          await onParticipantUpdate();
        }
      } else {
        onMessage(`Failed to cancel request: ${result.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Cancel request error:', error);
      onMessage('Failed to cancel request. Please try again.', 'error');
    } finally {
      setLoadingRequests(false);
    }
  };

  // Handle admin rotation click from summary - automatically send the required rotation birds
  const handleAdminRotationClickFromSummary = async () => {
    try {
      setLoadingRequests(true);
      
      const rotationBirds = getRequiredAdminRotationBirds;
      
      // Send request directly with the calculated rotation birds
      const result = await sendHandlerRequest(participant.id, 'admin-rotation-mandatory', {
        numberOfBirds: rotationBirds,
        isAdminRotation: true,
        IsMandatoryRotation: true // This is a mandatory rotation, cannot be cancelled
      });
      
      if (result && result.success) {
        onMessage(`Successfully sent ${rotationBirds} birds to admin rotation! An administrator will review and assign your birds.`);
        await loadBreederRequests(); // Reload requests
      } else {
        console.error('Admin rotation request failed:', result);
        onMessage(`Failed to send admin rotation request: ${result?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending admin rotation request:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      onMessage(`An error occurred while sending the admin rotation request: ${errorMsg}`);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Handle admin rotation click from section - open modal for custom amount
  const handleAdminRotationClick = () => {
    // Create a virtual admin handler for rotation
    const adminRotationHandler = {
      id: 'admin-rotation',
      fullName: 'Admin Rotation',
      loftName: 'System Placement',
      handlerInfo: {
        section: 'ROTATION',
        totalBirds: 999999, // No limit
        reserved: 0,
        openSlots: 999999
      }
    };
    
    setAdminHandler(adminRotationHandler);
    setShowAdminRotationModal(true);
  };

  // Handle admin rotation request submission
  const handleSubmitAdminRotation = async (requestData) => {
    try {
      // Send request to admin-rotation ID with isAdminRotation flag
      const result = await sendHandlerRequest(participant.id, 'admin-rotation', {
        ...requestData,
        isAdminRotation: true
      });
      
      if (result && result.success) {
        onMessage('Admin rotation request sent successfully! An administrator will review and assign your birds.');
        await loadBreederRequests(); // Reload requests
        setShowAdminRotationModal(false);
        setAdminHandler(null);
      } else {
        console.error('Admin rotation request failed:', result);
        onMessage(`Failed to send admin rotation request: ${result?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending admin rotation request:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      onMessage(`An error occurred while sending the admin rotation request: ${errorMsg}`);
    }
  };

  const handleSave = useCallback(async () => {
    console.log('BreederTab handleSave called', { isEditing, breederData, participant });
    
    if (!isEditing) {
      console.log('Not in editing mode, returning');
      return;
    }
    
    try {
      console.log('Setting saving to true');
      setSaving(true);
      
      const birdCalc = calculateBirds;
      console.log('Bird calculation:', birdCalc);
      
      if (!birdCalc.isValid) {
        if (birdCalc.total < 5) {
          onMessage('Minimum 5 birds required for race participation.', 'error');
        } else {
          onMessage('Bird numbers are invalid.', 'error');
        }
        setSaving(false);
        return;
      }
      
      const updatedParticipant = {
        ...participant,
        breederInfo: {
          ...participant.breederInfo,
          totalBirdsForRace: parseInt(breederData.totalBirds) || 0
        },
        totalBirds: parseInt(breederData.totalBirds) || 0, // Keep for backward compatibility
        birdsToRotation: birdCalc.toRotation, // Use calculated rotation value
        handlerRequests: breederData.handlerRequests
      };
      
      const authData = getLoggedInParticipant();
      const result = await SaveParticipant(updatedParticipant, authData?.authToken);
      
      // API returns participant object directly, check if successful
      if (result && result.id) {
        // Call the callback to reload data
        if (typeof onParticipantUpdate === 'function') {
          await onParticipantUpdate();
        }
      } else {
        throw new Error('Save failed - no participant data returned');
      }
      
      setOriginalBreederData(breederData);
      setIsTotalBirdsSaved(true);
      setHasUnsavedChanges(false);
      onEditingChange(false);
      onMessage('Breeder information saved successfully!', 'success');
    } catch (error) {
      console.error('BreederTab save error:', error);
      onMessage('Failed to save breeder information. Please try again.', 'error');
    } finally {
      console.log('Setting saving to false');
      setSaving(false);
    }
  }, [isEditing, breederData, participant, onParticipantUpdate, onMessage, onEditingChange, setSaving, setOriginalBreederData, setIsTotalBirdsSaved, setHasUnsavedChanges, calculateBirds]);

  // Load breeder requests from server
  const loadBreederRequests = useCallback(async (force = false) => {
    if (!participant?.id) return;
    if (loadingRequests && !force) {
      return;
    }
    
    try {
      setLoadingRequests(true);
      const requestsResult = await getHandlerRequestsByBreederId(participant.id);

      let requests = [];
      if (requestsResult && Array.isArray(requestsResult.requests)) {
        requests = requestsResult.requests;
      } else if (Array.isArray(requestsResult)) {
        requests = requestsResult;
      }
      
      // Trust server response - no client-side filtering needed
      setBreederData(prev => ({
        ...prev,
        handlerRequests: requests
      }));
      setRequestsLoaded(true);
    } catch (error) {
      console.error('Error loading breeder requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  }, [participant?.id, loadingRequests]);

  // Cache role check to prevent unnecessary re-renders
  const isBreeder = useMemo(() => {
    return hasRole('breeder');
  }, [participant?.role]);

  // Load requests when component mounts or participant changes (only once per participant)
  useEffect(() => {
    if (participant?.id && isBreeder && !requestsLoaded) {
      loadBreederRequests();
    }
  }, [participant?.id, isBreeder, requestsLoaded, loadBreederRequests]);

  // Reset requests loaded flag when participant changes
  useEffect(() => {
    setRequestsLoaded(false);
  }, [participant?.id]);

  // Set up global save function for ModernDashboard
  useEffect(() => {
    window.breederTabSave = handleSave;
    return () => {
      delete window.breederTabSave;
    };
  }, [handleSave]);

  const handleCancel = () => {
    setBreederData(originalBreederData);
    setIsTotalBirdsSaved(!!participant.totalBirds);
    setHasUnsavedChanges(false);
    // Don't call onEditingChange here - let global system handle it
  };

  const toggleRowExpanded = (handlerId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(handlerId)) {
      newExpanded.delete(handlerId);
    } else {
      newExpanded.add(handlerId);
    }
    setExpandedRows(newExpanded);
  };

  // Memoized calculations - must be before early return
  const birdCalc = calculateBirds;
  const pendingRequests = useMemo(() => breederData.handlerRequests.filter(r => r.status === 'pending'), [breederData.handlerRequests]);
  const approvedRequests = useMemo(() => breederData.handlerRequests.filter(r => r.status === 'approved'), [breederData.handlerRequests]);

  if (!hasRole('breeder')) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        You need to have the Breeder role to access this section. You can add the Breeder role in the Personal Info tab.
      </div>
    );
  }

  return (
    <div className="container-fluid px-0">
      {/* Breeder Information Card */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-egg me-2"></i>
            Breeder Information
          </h5>
        </div>
        <div className="card-body">
          {isEditing && (
            <div className={`alert ${hasUnsavedChanges ? 'alert-warning' : 'alert-info'} d-flex align-items-center mb-3`}>
              <i className={`bi ${hasUnsavedChanges ? 'bi-exclamation-triangle' : 'bi-pencil-square'} me-2`}></i>
              <strong>{hasUnsavedChanges ? 'Unsaved Changes' : 'Edit Mode Active'}</strong> - 
              {hasUnsavedChanges ? 'Save your changes to continue' : 'Modify your breeder settings'}
            </div>
          )}

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-bold">Total Birds</label>
              <input
                type="number"
                className={`form-control ${!birdCalc.isValid ? 'is-invalid' : hasUnsavedChanges && isEditing ? 'border-warning' : ''}`}
                value={breederData.totalBirds}
                onChange={(e) => handleInputChange('totalBirds', e.target.value)}
                disabled={!isEditing}
                min="0"
                placeholder={isEditing ? "Enter total birds (minimum 5)" : ""}
              />
              <small className="text-muted d-block mt-1">
                <i className="bi bi-info-circle me-1"></i>
                Minimum 5 birds required for race participation
              </small>
              {!birdCalc.isValid && (
                <div className="invalid-feedback">
                  This field is required. Minimum 5 birds for race participation.
                </div>
              )}
            </div>
            <div className="col-md-6">
              {isEditing && hasUnsavedChanges && (
                <div className="alert alert-warning mt-4 mb-0 py-2">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <small><strong>Unsaved changes!</strong> Click "Save" to save your changes.</small>
                </div>
              )}
              
              {!isEditing && !isTotalBirdsSaved && (
                <div className="alert alert-info mt-4 mb-0 py-2">
                  <i className="bi bi-info-circle me-2"></i>
                  <small>Save your total birds count to start sending handler requests.</small>
                </div>
              )}
            </div>
          </div>

          {/* Bird Distribution Dashboard */}
          {birdCalc.total > 0 && (
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white py-3">
                <h6 className="mb-0 fw-bold" style={{ color: '#0b3d91' }}>
                  <i className="bi bi-pie-chart-fill me-2"></i>
                  Bird Distribution Dashboard
                </h6>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  {/* 1. Total Birds to Enter */}
                  <div className="col-lg-4 col-md-6">
                    <div className="card bg-light border h-100">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="fs-3 fw-bold text-dark">{birdCalc.total}</div>
                            <div className="fw-medium text-dark">Total Birds declared for race</div>
                          </div>
                          <div className="bg-secondary bg-opacity-10 rounded p-2">
                            <i className="bi bi-grid-3x3 fs-4 text-secondary"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 2. Total Birds for Rotation */}
                  <div className="col-lg-4 col-md-6">
                    <div className="card bg-light border h-100">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="fs-3 fw-bold text-dark">{birdCalc.toRotation}</div>
                            <div className="fw-medium text-dark">Total Birds for Rotation</div>
                            <div className="small text-muted">Required admin rotation</div>
                          </div>
                          <div className="bg-secondary bg-opacity-10 rounded p-2">
                            <i className="bi bi-arrow-repeat fs-4 text-secondary"></i>
                          </div>
                        </div>
                        {(() => {
                          const mandatoryStatus = getMandatoryRotationStatus;
                          
                          if (mandatoryStatus.status === 'needed') {
                            return (
                              <div className="mt-3">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={handleAdminRotationClickFromSummary}
                                  disabled={loadingRequests || !isTotalBirdsSaved}
                                >
                                  <i className="bi bi-send me-1"></i>
                                  Send to Rotation
                                </button>
                              </div>
                            );
                          }
                          
                          if (mandatoryStatus.status === 'pending') {
                            return (
                              <div className="mt-3 small text-warning">
                                <i className="bi bi-clock me-1"></i>
                                Pending Admin Review
                              </div>
                            );
                          }
                          
                          if (mandatoryStatus.status === 'approved') {
                            return (
                              <div className="mt-3 small text-success">
                                <i className="bi bi-check-circle me-1"></i>
                                Approved - {mandatoryStatus.birds} birds
                              </div>
                            );
                          }
                          
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* 3. Un-assigned Birds */}
                  <div className="col-lg-4 col-md-6">
                    <div className="card bg-light border h-100">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="fs-3 fw-bold text-dark">{birdCalc.remainingForHandlers}</div>
                            <div className="fw-medium text-dark">Birds left to assign to Handlers</div>
                          </div>
                          <div className="bg-secondary bg-opacity-10 rounded p-2">
                            <i className="bi bi-plus-circle fs-4 text-secondary"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rotation Rules Information */}
                {birdCalc.total >= 5 && (
                  <div className="mt-3">
                    <h6 className="fw-bold mb-2">Admin Rotation Rules:</h6>
                    <ul className="mb-2">
                      <li><strong>5-11 birds:</strong> 1 bird must go to admin rotation</li>
                      <li><strong>12-29 birds:</strong> 2 birds must go to admin rotation</li>
                      <li><strong>30+ birds:</strong> 3 birds must go to admin rotation</li>
                    </ul>
                    <p className="mb-1">• All non-specified birds will go into rotation</p>
                    <p className="mb-1">• Maximum 5 birds per breeder to handler per entry</p>
                    <p className="mb-0">• Rotation birds are assigned by administrators to available handlers</p>
                  </div>
                )}

                {/* Status Messages */}
                {birdCalc.total > 0 && birdCalc.total < 5 && (
                  <div className="mt-3 p-3 bg-warning bg-opacity-10 border-start border-warning border-3 rounded">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                      <small className="text-dark">
                        <strong>You need to enter at least 5 birds</strong> to participate in the race
                      </small>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}




        </div>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h6 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-list-check me-2"></i>
            My Handler Requests
          </h6>
        </div>
        <div className="card-body">
          {breederData.handlerRequests.length === 0 ? (
            <div className="text-center text-muted py-3">
              <i className="bi bi-inbox display-4 text-muted"></i>
              <p className="mt-2">No handler requests sent yet</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <table className="table table-hover mb-0 border rounded">
                    <thead className="table-dark">
                      <tr>
                        <th className="text-nowrap py-3">
                          <i className="bi bi-person-workspace me-2"></i>
                          Handler
                        </th>
                        <th className="text-nowrap text-center py-3" style={{width: '100px'}}>
                          <i className="bi bi-egg me-1"></i>
                          Birds
                        </th>
                        <th className="text-nowrap text-center py-3" style={{width: '100px'}}>
                          <i className="bi bi-check-circle me-1"></i>
                          Status
                        </th>
                        <th className="d-none d-md-table-cell text-nowrap text-center py-3" style={{width: '120px'}}>
                          <i className="bi bi-calendar me-1"></i>
                          Date Sent
                        </th>
                        <th className="text-center py-3" style={{width: '180px'}}>
                          <i className="bi bi-gear me-1"></i>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                  {breederData.handlerRequests.map(request => {
                    const handler = allParticipants?.find(p => p.id === request.handlerId);
                    const handlerName = (request.handlerId === 'admin-rotation' || request.handlerId === 'admin-rotation-mandatory' || request.isAdminRotation === true) 
                      ? 'Admin Rotation' 
                      : (handler?.fullName || 'Unknown Handler');
                    return (
                      <tr key={request.id} className="align-middle">
                        <td>
                          <div>
                            <strong className="text-nowrap">
                              {handlerName}
                              {(request.IsMandatoryRotation === true || request.isMandatoryRotation === true) && handlerName === 'Admin Rotation' && ' (Mandatory)'}
                            </strong>
                            <div className="d-md-none small text-muted mt-1">
                              📅 {new Date(request.requestDate).toLocaleDateString()}
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-primary">{request.numberOfBirds}</span>
                        </td>
                        <td className="text-center">
                          <span className={`badge ${
                            request.status === 'pending' ? 'bg-warning' :
                            request.status === 'approved' ? 'bg-success' :
                            request.status === 'cancelled' ? 'bg-secondary' :
                            'bg-danger'
                          }`}>
                            {request.status === 'cancelled' ? 'Cancelled' : request.status}
                          </span>
                        </td>
                        <td className="d-none d-md-table-cell text-center text-nowrap">{new Date(request.requestDate).toLocaleDateString()}</td>
                        <td className="text-center">
                          <div className="d-flex gap-1 justify-content-center">

                            
                            {/* Always show Add Bird Info for approved requests or mandatory rotation requests */}
                            {(request.status === 'approved' || request.IsMandatoryRotation || request.isMandatoryRotation) && (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleViewBirdInfo(request)}
                                title="Add/edit bird information"
                              >
                                <i className="bi bi-plus-circle"></i>
                                <span className="d-none d-lg-inline ms-1">Add Bird Info</span>
                              </button>
                            )}
                            {/* Only show Cancel button for pending requests that are NOT mandatory */}
                            {request.status === 'pending' && !request.IsMandatoryRotation && !request.isMandatoryRotation && (
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleCancelRequest(request.id)}
                                disabled={loadingRequests}
                                title="Cancel this request"
                              >
                                <i className="bi bi-x-circle"></i>
                                <span className="d-none d-sm-inline ms-1">Cancel</span>
                              </button>
                            )}

                          </div>
                        </td>
                      </tr>
                    );
                  })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none">
                {breederData.handlerRequests.map(request => {
                  const handler = allParticipants?.find(p => p.id === request.handlerId);
                  const handlerName = (request.handlerId === 'admin-rotation' || request.handlerId === 'admin-rotation-mandatory' || request.isAdminRotation === true) 
                    ? 'Admin Rotation' 
                    : (handler?.fullName || 'Unknown Handler');
                  return (
                    <div key={request.id} className="border-bottom">
                      <div className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold">
                              {handlerName}
                              {(request.IsMandatoryRotation === true || request.isMandatoryRotation === true) && handlerName === 'Admin Rotation' && ' (Mandatory)'}
                            </h6>
                            <div className="small text-muted">
                              📅 {new Date(request.requestDate).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-end">
                            <span className={`badge ${
                              request.status === 'pending' ? 'bg-warning' :
                              request.status === 'approved' ? 'bg-success' :
                              request.status === 'cancelled' ? 'bg-secondary' :
                              'bg-danger'
                            } mb-1`}>
                              {request.status === 'cancelled' ? 'Cancelled' : request.status}
                            </span>
                            <div>
                              <span className="badge bg-primary">{request.numberOfBirds} birds</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="d-flex flex-column gap-2">
                          {(request.status === 'approved' || request.IsMandatoryRotation || request.isMandatoryRotation) && (
                            <button
                              className="btn btn-outline-primary w-100"
                              onClick={() => handleViewBirdInfo(request)}
                              style={{ minHeight: '44px' }}
                            >
                              <i className="bi bi-plus-circle me-2"></i>
                              Add Bird Information
                            </button>
                          )}
                          {request.status === 'pending' && !request.IsMandatoryRotation && !request.isMandatoryRotation && (
                            <button
                              className="btn btn-outline-danger w-100"
                              onClick={() => handleCancelRequest(request.id)}
                              disabled={loadingRequests}
                              style={{ minHeight: '44px' }}
                            >
                              <i className="bi bi-x-circle me-2"></i>
                              Cancel Request
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Available Handlers */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <h6 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-people me-2"></i>
            Available Handlers in Section {participant?.section}
          </h6>
          <span className="badge bg-primary">{getAvailableHandlers.length} handlers</span>
        </div>
        <div className="card-body">
          {getAvailableHandlers.length === 0 ? (
            <div className="text-center text-muted py-3">
              <i className="bi bi-inbox display-4 text-muted"></i>
              <p className="mt-2">No handlers available in your section</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <table className="table table-hover mb-0 border rounded">
                    <thead className="table-primary">
                      <tr>
                        <th style={{width: '50px'}} className="py-3"></th>
                        <th className="text-nowrap py-3">
                          <i className="bi bi-person me-2"></i>
                          Handler Name
                        </th>
                        <th className="d-none d-md-table-cell text-nowrap py-3">
                          <i className="bi bi-house me-1"></i>
                          Loft Name
                        </th>
                        <th className="d-none d-lg-table-cell text-nowrap py-3">
                          <i className="bi bi-geo-alt me-1"></i>
                          Section
                        </th>
                        <th className="d-none d-lg-table-cell text-nowrap py-3">
                          <i className="bi bi-ruler me-1"></i>
                          Distance (mi)
                        </th>
                        <th className="text-nowrap py-3">
                          <i className="bi bi-info-circle me-1"></i>
                          Availability
                        </th>
                        <th className="text-nowrap py-3" style={{width: '150px'}}>
                          <i className="bi bi-tools me-1"></i>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                  {getAvailableHandlers.map(handler => {
                    const requestStatus = getRequestStatus(handler.id);
                    const isExpanded = expandedRows.has(handler.id);
                    return (
                      <React.Fragment key={handler.id}>
                        <tr className="align-middle">
                          <td>
                            <button
                              className="btn btn-sm btn-outline-info d-flex align-items-center gap-1"
                              onClick={() => toggleRowExpanded(handler.id)}
                              title={isExpanded ? 'Hide details' : 'Show more details'}
                            >
                              <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                              <span className="d-none d-md-inline small">
                                {isExpanded ? 'Less' : 'Details'}
                              </span>
                            </button>
                          </td>
                          <td>
                            <div>
                              <strong className="text-nowrap">{handler.fullName}</strong>
                              <div className="d-md-none small text-muted mt-1">
                                {handler.loftName && <div>🏠 {handler.loftName}</div>}
                                <div>📍 Section {handler.handlerInfo?.section || 'N/A'} • {(() => {
                                  const distance = handler.handlerInfo?.distanceFromRaceStation || handler.handlerInfo?.distance;
                                  if (!distance) return 'No distance data';
                                  return String(distance).replace(/ miles?$/i, '').replace(/ mi$/i, '');
                                })()}</div>
                              </div>
                            </div>
                          </td>
                          <td className="d-none d-md-table-cell text-nowrap">{handler.loftName || '-'}</td>
                          <td className="d-none d-lg-table-cell text-center">{handler.handlerInfo?.section || '-'}</td>
                          <td className="d-none d-lg-table-cell text-nowrap">{(() => {
                            const distance = handler.handlerInfo?.distanceFromRaceStation || handler.handlerInfo?.distance;
                            if (!distance) return '-';
                            return String(distance).replace(/ miles?$/i, '').replace(/ mi$/i, '');
                          })()}</td>
                          <td className="text-center">
                            {requestStatus === 'pending' && (
                              <span className="badge bg-warning text-dark px-3 py-2">
                                <i className="bi bi-clock me-1"></i>
                                Pending
                              </span>
                            )}
                            {requestStatus === 'approved' && (
                              <span className="badge bg-success px-3 py-2">
                                <i className="bi bi-check-circle me-1"></i>
                                Approved
                              </span>
                            )}
                            {requestStatus === 'none' && (
                              <span className="text-success fw-medium px-3 py-2">
                                <i className="bi bi-plus-circle me-1"></i>
                                Available
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              {requestStatus === 'none' && (
                                <button
                                  className="btn btn-outline-primary btn-sm text-nowrap"
                                  onClick={() => {
                                    setSelectedHandler(handler);
                                    setShowRequestModal(true);
                                  }}
                                  disabled={loadingRequests || !isTotalBirdsSaved || getRemainingBirdSlotsForHandler(handler.id) <= 0 || getRemainingBirdsForHandlers <= 0}
                                  title={!isTotalBirdsSaved ? 'Save total birds first' : getRemainingBirdSlotsForHandler(handler.id) <= 0 ? 'Handler has reached 5 bird limit' : getRemainingBirdsForHandlers <= 0 ? 'No more birds available for handlers' : 'Send request to this handler'}
                                >
                                  <i className="bi bi-envelope me-1"></i>
                                  <span className="d-none d-sm-inline">Send Request</span>
                                  <span className="d-sm-none">Send</span>
                                </button>
                              )}
                              
                              {requestStatus === 'pending' && (
                                <button
                                  className="btn btn-outline-danger btn-sm text-nowrap"
                                  onClick={() => {
                                    const request = breederData.handlerRequests.find(r => r.handlerId === handler.id);
                                    if (request) handleCancelRequest(request.id);
                                  }}
                                  disabled={loadingRequests}
                                >
                                  <i className="bi bi-x-circle me-1"></i>
                                  <span className="d-none d-sm-inline">Cancel</span>
                                  <span className="d-sm-none">×</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td></td>
                            <td colSpan="6">
                              <div className="bg-light p-3 rounded">
                                <div className="row">
                                  <div className="col-md-6">
                                    <strong>Contact Information:</strong>
                                    <div className="mt-2">
                                      <div><i className="bi bi-envelope me-2"></i>{handler.emailAddress || 'No email provided'}</div>
                                      <div><i className="bi bi-phone me-2"></i>{handler.mobile || 'No mobile provided'}</div>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <strong>Additional Info:</strong>
                                    <div className="mt-2">
                                      <div><i className="bi bi-geo-alt me-2"></i>Section: {handler.handlerInfo?.section || 'Not specified'}</div>
                                      <div><i className="bi bi-speedometer2 me-2"></i>Distance: {(() => {
                                        const distance = handler.handlerInfo?.distanceFromRaceStation || handler.handlerInfo?.distance;
                                        if (!distance) return 'Not specified';
                                        return String(distance).replace(/ miles?$/i, '').replace(/ mi$/i, '');
                                      })()}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none">
                {getAvailableHandlers.map(handler => {
                  const requestStatus = getRequestStatus(handler.id);
                  const isExpanded = expandedRows.has(handler.id);
                  return (
                    <div key={handler.id} className="border-bottom">
                      <div className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold">{handler.fullName}</h6>
                            <div className="small text-muted mb-1">{handler.loftName || '-'}</div>
                            <div className="small">
                              📍 {handler.handlerInfo?.section || '-'} • 📏 {(() => {
                                const distance = handler.handlerInfo?.distanceFromRaceStation || handler.handlerInfo?.distance;
                                if (!distance) return 'N/A';
                                return String(distance).replace(/ miles?$/i, '').replace(/ mi$/i, '') + ' mi';
                              })()} 
                            </div>
                          </div>
                          <div className="text-end">
                            {requestStatus === 'pending' && (
                              <span className="badge bg-warning text-dark px-3 py-2">
                                <i className="bi bi-clock me-1"></i>
                                Pending
                              </span>
                            )}
                            {requestStatus === 'approved' && (
                              <span className="badge bg-success px-3 py-2">
                                <i className="bi bi-check-circle me-1"></i>
                                Approved
                              </span>
                            )}
                            {requestStatus === 'none' && (
                              <span className="text-success fw-medium px-3 py-2">
                                <i className="bi bi-plus-circle me-1"></i>
                                Available
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="d-flex flex-column flex-sm-row gap-2">
                          <button
                            className="btn btn-outline-info flex-grow-1"
                            onClick={() => toggleRowExpanded(handler.id)}
                            style={{ minHeight: '44px' }}
                          >
                            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-1`}></i>
                            {isExpanded ? 'Hide' : 'Show'} Details
                          </button>
                          
                          {requestStatus === 'pending' && (
                            <button className="btn btn-outline-warning flex-grow-1" disabled style={{ minHeight: '44px' }}>
                              <i className="bi bi-clock me-1"></i>
                              Pending Request
                            </button>
                          )}
                          {requestStatus === 'approved' && (
                            <button className="btn btn-outline-success flex-grow-1" disabled style={{ minHeight: '44px' }}>
                              <i className="bi bi-check-circle me-1"></i>
                              Approved
                            </button>
                          )}
                          {requestStatus === 'none' && (
                            <button
                              className="btn btn-primary flex-grow-1"
                              onClick={() => {
                                setSelectedHandler(handler);
                                setShowRequestModal(true);
                              }}
                              disabled={loadingRequests || !isTotalBirdsSaved || getRemainingBirdSlotsForHandler(handler.id) <= 0 || getRemainingBirdsForHandlers <= 0}
                              title={!isTotalBirdsSaved ? 'Save total birds first' : getRemainingBirdSlotsForHandler(handler.id) <= 0 ? 'Handler has reached 5 bird limit' : getRemainingBirdsForHandlers <= 0 ? 'No more birds available for handlers' : 'Send request to this handler'}
                              style={{ minHeight: '44px' }}
                            >
                              <i className="bi bi-envelope me-1"></i>
                              Send Request
                            </button>
                          )}
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="mt-3 p-3 bg-light rounded">
                            <div className="mb-3">
                              <div className="small fw-bold text-muted mb-2">CONTACT INFORMATION</div>
                              <div className="small">
                                <div className="mb-1">
                                  <i className="bi bi-envelope me-2"></i>
                                  {handler.emailAddress || 'No email provided'}
                                </div>
                                <div>
                                  <i className="bi bi-phone me-2"></i>
                                  {handler.mobile || 'No mobile provided'}
                                </div>
                              </div>
                            </div>
                            {handler.handlerInfo?.breederApplicationMethod && (
                              <div>
                                <div className="small fw-bold text-muted mb-2">CONTACT METHODS</div>
                                <div className="d-flex gap-1 flex-wrap">
                                  <span className="badge bg-primary">In-App</span>
                                  {handler.handlerInfo.breederApplicationMethod.includes('text') && (
                                    <span className="badge bg-info">Text</span>
                                  )}
                                  {handler.handlerInfo.breederApplicationMethod.includes('email') && (
                                    <span className="badge bg-info">Email</span>
                                  )}
                                  {handler.handlerInfo.breederApplicationMethod.includes('call') && (
                                    <span className="badge bg-info">Call</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Admin Rotation Section */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h6 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-arrow-repeat me-2"></i>
            Admin Rotation (System Placement)
          </h6>
        </div>
        <div className="card-body">
          <div className="list-group">
            <div className="list-group-item" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #0b3d91' }}>
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center">
                <div className="flex-grow-1 mb-3 mb-lg-0">
                  <strong>
                    <i className="bi bi-arrow-repeat me-2" style={{ color: '#0b3d91' }}></i>
                    Admin Rotation (System Placement)
                  </strong>
                  <div className="small mt-1">
                    <span className="text-muted">No handler preference - Admin will assign</span>
                    <span className="d-none d-sm-inline mx-2">|</span>
                    <span className="d-sm-none d-block mt-1"></span>
                    <span className="text-info fw-bold">No Bird Limits</span>
                  </div>
                  <div className="small text-muted mt-1">
                    <i className="bi bi-info-circle me-1"></i>
                    Select this option if you want administrators to assign your birds to available handlers
                  </div>
                </div>
                <button 
                  className="btn btn-sm text-nowrap align-self-stretch align-self-lg-auto"
                  style={{ backgroundColor: '#0b3d91', color: 'white', minWidth: '140px' }}
                  onClick={handleAdminRotationClick}
                  disabled={loadingRequests || !isTotalBirdsSaved}
                  title={!isTotalBirdsSaved ? 'Save total birds first' : 'Request admin rotation'}
                >
                  <i className="bi bi-arrow-repeat me-1"></i>
                  Request Rotation
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Request Modal */}
      {showRequestModal && selectedHandler && (
        <BreederRequestModal
          show={showRequestModal}
          handler={selectedHandler}
          breeder={participant}
          onSubmit={handleSendRequest}
          onClose={() => {
            setShowRequestModal(false);
            setSelectedHandler(null);
          }}
          maxBirds={Math.min(
            getRemainingBirdSlotsForHandler(selectedHandler.id),
            getRemainingBirdsForHandlers
          )}
          approvedBirdsWithHandler={getApprovedBirdsForHandler(selectedHandler.id)}
        />
      )}

      {/* Admin Rotation Modal */}
      {showAdminRotationModal && adminHandler && (
        <BreederRequestModal
          show={showAdminRotationModal}
          handler={adminHandler}
          breeder={participant}
          onSubmit={handleSubmitAdminRotation}
          onClose={() => {
            setShowAdminRotationModal(false);
            setAdminHandler(null);
          }}
          maxBirds={999} // No limit for admin rotation
          approvedBirdsWithHandler={0}
        />
      )}

      {/* Bird Details Modal */}
      {showBirdModal && selectedRequestForBirds && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowBirdModal(false)}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">
                  <i className="bi bi-plus-circle me-2"></i>
                  Add/Edit Bird Information
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowBirdModal(false)}
                ></button>
              </div>
              
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Handler:</strong> {(() => {
                      if (selectedRequestForBirds.handlerId === 'admin-rotation' || selectedRequestForBirds.handlerId === 'admin-rotation-mandatory' || selectedRequestForBirds.isAdminRotation === true) {
                        return 'Admin Rotation';
                      }
                      const handler = allParticipants?.find(p => p.id === selectedRequestForBirds.handlerId);
                      return handler ? handler.fullName : 'Unknown Handler';
                    })()}
                  </div>
                  <div className="col-md-6">
                    <strong>Birds Requested:</strong> {selectedRequestForBirds.numberOfBirds || selectedRequestForBirds.birdsRequested}
                  </div>
                  <div className="col-md-6">
                    <strong>Request Date:</strong> {new Date(selectedRequestForBirds.requestDate).toLocaleDateString()}
                  </div>
                  <div className="col-md-6">
                    <strong>Status:</strong> <span className={`badge ${
                      selectedRequestForBirds.status === 'approved' ? 'bg-success' :
                      selectedRequestForBirds.status === 'pending' ? 'bg-warning' :
                      'bg-secondary'
                    }`}>{selectedRequestForBirds.status}</span>
                  </div>
                </div>

                <hr />

                <h6 className="fw-bold mb-3">
                  <i className="bi bi-list-stars me-2"></i>
                  Bird Information
                </h6>
                
                {(() => {
                  const birds = getBirdsInfo(selectedRequestForBirds);
                  
                  if (birds.length > 0) {
                    return (
                      <div className="table-responsive">
                        <table className="table table-striped">
                          <thead className="table-light">
                            <tr>
                              <th>Band Number</th>
                              <th>Color</th>
                              <th>Sex</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {birds.map((bird, index) => (
                              <tr key={index}>
                                <td>
                                  <code className="text-dark bg-light px-2 py-1 rounded">
                                    {bird.bandNumber || '[To be added]'}
                                  </code>
                                </td>
                                <td>{bird.color || '[To be added]'}</td>
                                <td>{bird.sex || '[To be added]'}</td>
                                <td>
                                  {bird.status === 'confirmed' ? (
                                    <span className="badge bg-success">Confirmed</span>
                                  ) : (
                                    <span className="badge bg-warning text-dark">Placeholder</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  } else {
                    return (
                      <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>Ready to add bird information!</strong>
                        <br />
                        This is where you'll be able to add detailed bird information (band numbers, colors, sex, etc.) for the birds going to this handler.
                        <div className="mt-3">
                          <button className="btn btn-primary btn-sm" disabled>
                            <i className="bi bi-plus-circle me-1"></i>
                            Add Bird Details (Coming Soon)
                          </button>
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
              
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowBirdModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}